import React, { useContext, useEffect, useMemo, useState } from "react";
import { launchableContextDefaultValues } from "tests/defaultValues";
import agent, {
  LaunchableAnalyticsForDate,
  LaunchableDailyAnalytics,
  LaunchableFromAnalytics,
  LaunchableFromList,
} from "server";
import { monthNames, timeout } from "components/utils";
import { OrgContext } from "./OrgContext";
import { NotificationContext } from "./NotificationContext";
import { AxiosRes, BaseRes } from "server/config";
import { processLaunchableData } from "components/Environment/Launchables/utils";

export interface UserMetrics {
  [userId: string]: {
    totalViews: number;
    totalDeploys: number;
  };
}

export interface ILaunchableContext {
  activelaunchables: LaunchableFromList[];
  loadingActiveLaunchables: boolean;
  refreshActiveLaunchables: () => any;
  deleteLaunchable: (orgID: string, id: string) => any;
}

export const LaunchableContext = React.createContext<ILaunchableContext>(
  launchableContextDefaultValues
);

export interface DailyLaunchableMetrics {
  date: string;
  [key: string]: string | number;
}

export interface DailyOverviewLaunchableMetrics {
  date: string;
  Views: number;
  Deploys: number;
}

export const formatDailyLaunchableMetrics = (date: string) => {
  const transformDate = date.split("-");
  return `${monthNames[Number(transformDate[1]) - 1]} ${Number(
    transformDate[2]
  )}`;
};

interface Props {
  children: React.ReactNode;
}
const LaunchableContextProvider: React.FC<Props> = ({ children }) => {
  const orgContext = useContext(OrgContext);
  const notificationContext = useContext(NotificationContext);
  // Calls API to get Launchables for the active Org
  const [activelaunchables, setActiveLaunchables] = useState<
    LaunchableFromList[]
  >([]);
  const [loadingActiveLaunchables, setLoadingActiveLaunchables] =
    useState<boolean>(false);

  const listLaunchables = async (activeOrgId: string) => {
    setLoadingActiveLaunchables(true);
    const list = await agent.Launchables.listLaunchables(activeOrgId);
    if (list.data) {
      setActiveLaunchables(list.data);
    } else {
      setActiveLaunchables([]);
    }
    setLoadingActiveLaunchables(false);
  };

  useEffect(() => {
    if (orgContext.activeOrgId !== "") {
      listLaunchables(orgContext.activeOrgId);
    }
  }, [orgContext.activeOrgId]);

  const refreshActiveLaunchables = async () => {
    await listLaunchables(orgContext.activeOrgId);
  };

  const deleteLaunchable = async (
    orgID: string,
    id: string
  ): Promise<AxiosRes & BaseRes> => {
    const res = await agent.Launchables.deleteLaunchable(orgID, id);
    if (res.success) {
      const newLaunchable = activelaunchables.filter((l) => l.id !== id);
      setActiveLaunchables(newLaunchable);
    } else {
      notificationContext.showNotification(
        "Error Deleting Launchable",
        res.message,
        "error"
      );
    }
    return res;
  };

  const providerData = {
    activelaunchables,
    loadingActiveLaunchables,
    refreshActiveLaunchables,
    deleteLaunchable,
  };

  return (
    <LaunchableContext.Provider value={providerData}>
      {children}
    </LaunchableContext.Provider>
  );
};

export default LaunchableContextProvider;
